import { useEffect, useRef, useState } from "react";
import "./introSection.css";

const useScrollVisible = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isVisible];
};

/* ── Problem Section ── */
const ProblemSection = () => {
  const [ref, isVisible] = useScrollVisible();

  return (
    <section className="problem-section" ref={ref}>
      <div className={`problem-container is-fade ${isVisible ? "is-visible" : ""}`}>
        <p className="is-eyebrow">The Problem</p>
        <h2 className="is-headline">Two platforms. One broken workflow.</h2>
        <p className="is-subtext">
          Sacrifice accuracy, or juggle two tools.
        </p>

        <div className="problem-cards">
          <div className="problem-card">
            <div className="problem-card-header problem-card-header--excel">
              <span className="problem-card-platform">Model in Excel</span>
            </div>
            <ul className="problem-list">
              <li className="item-pro">No separate software required</li>
              <li className="item-pro">Flexible and integrated into your workflow</li>
              <li className="item-con">Breaks down under multi-tenant lease complexity</li>
              <li className="item-con">High risk of undetected formula and input errors</li>
              <li className="item-con">No standardized institutional outputs</li>
            </ul>
          </div>

          <div className="problem-divider">
            <span className="problem-vs">vs</span>
          </div>

          <div className="problem-card">
            <div className="problem-card-header problem-card-header--argus">
              <span className="problem-card-platform">Legacy software + Excel</span>
            </div>
            <ul className="problem-list">
              <li className="item-pro">Standardized, institutional-grade outputs</li>
              <li className="item-pro">Handles complex multi-tenant lease structures</li>
              <li className="item-con">Change an input, re-export to Excel, relink, repeat</li>
              <li className="item-con">Black-box outputs you can’t adjust</li>
              <li className="item-con">Costly per-seat licensing</li>
            </ul>
          </div>
        </div>

        <p className="problem-resolution">
          That tradeoff has always been accepted.{" "}
          <span className="problem-resolution-accent">It doesn't have to be.</span>
        </p>
      </div>
    </section>
  );
};

/* ── Solution / Features Section ── */
const features = [
  {
    index: "01",
    title: "Institutional-Grade Calculations",
    body: "The same precision as dedicated underwriting software. Consistent, reliable outputs on every deal, without fragile formulas.",
  },
  {
    index: "02",
    title: "Excel-Native Workflow",
    body: "Link Khor to your own models and everything updates instantly. No re‑exporting, no relinking. Share it like any Excel file.",
  },
  {
    index: "03",
    title: "Full Control Over Outputs",
    body: "Override any Khor-generated value with your own Excel formula. Khor integrates it into the software and recalculates all dependent values automatically.",
  },
];

const SolutionSection = () => {
  const [ref, isVisible] = useScrollVisible();

  return (
    <section className="solution-section" ref={ref}>
      <div className={`solution-container is-fade ${isVisible ? "is-visible" : ""}`}>
        <p className="is-eyebrow">The Solution</p>
        <h2 className="is-headline">One model. One platform. Inside Excel.</h2>
        <p className="is-subtext is-subtext-tight">
          Khor Developer is an Excel add-in that runs institutional-grade CRE calculations
          behind the spreadsheets you already use.
        </p>
        <p className="is-subtext is-subtext-punch">
          Excel is the interface. Khor is the engine.
        </p>

        <div className="features-grid">
          {features.map((f) => (
            <div className="feature-card" key={f.index}>
              <span className="feature-index">{f.index}</span>
              <h3 className="feature-title">{f.title}</h3>
              <p className="feature-body">{f.body}</p>
            </div>
          ))}
        </div>

        <div className="argus-compat">
          <div className="argus-badge">ARGUS Enterprise Compatible</div>
          <p className="argus-text">
            Upload an ARGUS Enterprise file and get a fully functional Khor-powered model in
            Excel, ready to use.
          </p>
          <p className="argus-disclaimer">
            Not affiliated with or endorsed by Altus Group Ltd., maker of ARGUS Enterprise.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ── Demo Carousel Section ── */
const slides = [
  {
    video: "/Website_Slide1.mp4",
    caption: "Enter property inputs directly in Excel",
    body: "Input lease details, rent schedules, and assumptions directly inside Excel — no separate application required.",
  },
  {
    video: "/Website_Slide2.mp4",
    caption: "Khor's calculation engine runs in the background — results delivered back to Excel",
    body: "The calculation engine handles complex multi-tenant lease structures and produces institutional-grade cash flow projections automatically.",
  },
  {
    excelSlide: true,
    caption: "Link your own worksheets  — create one complete underwriting workflow.",
    bullets: [
      "Send, version, and review models exactly as you would any .xlsx file.",
      "Link your own worksheets to Khor input or output sheets to create one complete underwriting workflow",
    ],
  },
];

const DemoCarousel = () => {
  const [active, setActive] = useState(0);
  const [flowCompleted, setFlowCompleted] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [waitKey, setWaitKey] = useState(0);
  // null = no overlay | 'play' = waiting for first play | 'replay' = post-flow
  const [overlayMode, setOverlayMode] = useState(null);
  const video0Ref = useRef(null);
  const video1Ref = useRef(null);
  const waitTimerRef = useRef(null);
  const trackRef = useRef(null);
  const [sectionRef, isVisible] = useScrollVisible();
  const flowStarted = useRef(false);

  const handleVideoMetadata = (e) => {
    const { videoWidth, videoHeight } = e.target;
    if (videoWidth && videoHeight && trackRef.current) {
      trackRef.current.style.aspectRatio = `${videoWidth} / ${videoHeight}`;
    }
  };

  const videoRefs = [video0Ref, video1Ref];

  const pauseAll = () => videoRefs.forEach(r => { if (r.current) r.current.pause(); });

  // Try autoplay; fall back to showing the play button if blocked
  const tryPlay = (index) => {
    const r = videoRefs[index];
    if (r && r.current) {
      r.current.currentTime = 0;
      r.current.play()
        .then(() => setOverlayMode(null))
        .catch(() => setOverlayMode('play'));
    }
  };

  // Show play button as soon as the section enters view, then attempt autoplay
  useEffect(() => {
    if (isVisible && !flowStarted.current) {
      flowStarted.current = true;
      setOverlayMode('play');
      tryPlay(0);
    }
  }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearTimeout(waitTimerRef.current), []);

  const beginWait = (fromSlide) => {
    setIsWaiting(true);
    setWaitKey(k => k + 1);
    waitTimerRef.current = setTimeout(() => {
      const next = fromSlide + 1;
      setIsWaiting(false);
      setActive(next);
      if (next === 2) {
        setFlowCompleted(true);
        setOverlayMode(null);
      } else {
        // Try to autoplay the next video slide; show play button if blocked
        setOverlayMode('play');
        tryPlay(next);
      }
    }, 5000);
  };

  const handleVideoEnded = (slideIndex) => {
    if (flowCompleted) {
      setOverlayMode('replay');
    } else {
      beginWait(slideIndex);
    }
  };

  // Unified handler for both the initial play button and post-flow replay
  const handleOverlayClick = () => {
    const r = videoRefs[active];
    if (r && r.current) {
      r.current.currentTime = 0;
      r.current.play()
        .then(() => setOverlayMode(null))
        .catch(() => {});
    }
  };

  const goTo = (index) => {
    clearTimeout(waitTimerRef.current);
    setIsWaiting(false);
    pauseAll();
    setActive(index);
    if (index >= 2) {
      setOverlayMode(null);
    } else if (flowCompleted) {
      setOverlayMode('replay');
    } else {
      setOverlayMode('play');
    }
  };

  const getState = (index) => {
    const diff = ((index - active) % slides.length + slides.length) % slides.length;
    if (diff === 0) return "active";
    if (diff === 1) return "right";
    return "left";
  };

  const slide = slides[active];

  return (
    <section className="demo-section" ref={sectionRef}>
      <div className={`demo-container is-fade ${isVisible ? "is-visible" : ""}`}>
        <p className="is-eyebrow">See It In Action</p>
        <h2 className="is-headline">An Excel-native CRE platform.</h2>

        <div className="demo-caption-area" key={active}>
          <p className="demo-caption-title">{slide.caption}</p>
        </div>

        <div className="demo-controls">
          <button
            className="demo-arrow"
            onClick={() => goTo((active - 1 + slides.length) % slides.length)}
            aria-label="Previous slide"
          >&#8249;</button>
          <div className="demo-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`demo-dot ${i === active ? "demo-dot--active" : ""}`}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
          <button
            className="demo-arrow"
            onClick={() => goTo((active + 1) % slides.length)}
            aria-label="Next slide"
          >&#8250;</button>
        </div>

        <div className="demo-carousel">
          <div className="demo-track" ref={trackRef}>
            {slides.map((s, i) => {
              const state = getState(i);
              return (
                <div
                  key={i}
                  className="demo-slide"
                  data-state={state}
                  onClick={() => state !== "active" ? goTo(i) : undefined}
                >
                  {s.video ? (
                    <div className="demo-video-wrap">
                      <video
                        ref={videoRefs[i]}
                        src={s.video}
                        muted
                        playsInline
                        preload="auto"
                        className="demo-video"
                        onLoadedMetadata={handleVideoMetadata}
                        onEnded={() => handleVideoEnded(i)}
                      />

                      {/* 5-second countdown bar — animates while waiting to advance */}
                      {state === "active" && isWaiting && (
                        <div className="demo-countdown-bar" key={waitKey} />
                      )}

                      {/* Play / Replay overlay */}
                      {state === "active" && overlayMode && (
                        <button
                          className="demo-play-overlay"
                          onClick={(e) => { e.stopPropagation(); handleOverlayClick(); }}
                          aria-label={overlayMode === 'replay' ? 'Replay video' : 'Play video'}
                        >
                          <span className={`demo-play-circle ${overlayMode === 'play' ? 'demo-play-circle--play' : ''}`}>
                            {overlayMode === 'replay' ? '↺' : '▶'}
                          </span>
                          <span className="demo-play-label">
                            {overlayMode === 'replay' ? 'Replay' : 'Play'}
                          </span>
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="demo-integration-slide">
                      <div className="demo-integration-logos">
                        <img src="/logo_large.png" alt="Khor Developer" className="demo-integration-logo" />
                        <img src="/Link.png" alt="" className="demo-integration-sep" aria-hidden="true" />
                        <img src="/Microsoft-Excel-Logo.png" alt="Microsoft Excel" className="demo-integration-logo" />
                      </div>
                      <div className="demo-integration-img-wrap">
                        <img src="/Website_Slide3.png" alt="Khor Developer in Excel" className="demo-integration-screenshot" />
                        <div className="demo-zoom-bubble">
                          <p className="demo-zoom-label">Formula Bar</p>
                          <p className="demo-zoom-formula">=&#39;Property CF&#39;!HE1046/&#39;Present Value&#39;!G8</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );
};

const IntroSection = () => (
  <>
    <ProblemSection />
    <SolutionSection />
    <DemoCarousel />
  </>
);

export default IntroSection;
